import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HotToastService } from '@ngxpert/hot-toast';
import { catchError, throwError } from 'rxjs';

import { IdentityClientService, IdentityConstants } from '@auth';
import { ErrorService } from '@services';

const enum HttpStatusCode {
    BadRequest = 400,
    Unauthorized = 401,
    Forbidden = 403,
    NotFound = 404,
    Conflict = 409,
    UnsupportedMediaType = 415,
    SessionExpired = 419,
    Locked = 423,
    InternalServerError = 500,
    ServiceUnavailable = 503
};

const ERROR_ROUTE = 'error';
const SIGN_IN_ROUTE = 'signIn';
const UNAUTHORIZED_TOAST_ID = 'http-401';
const UNAUTHORIZED_MESSAGE = 'You are not authorised';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const notification = inject(HotToastService);
    const client = new IdentityClientService(new IdentityConstants()).get();
    const errorService = inject(ErrorService);
    const translate = inject(TranslateService);

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            const errorMessage = getErrorMessage(error.status);

            if (error.status !== HttpStatusCode.Conflict) {
                console.error(`HTTP error: ${errorMessage}`);
            }

            if (shouldNavigateToErrorPage(error.status)) {
                errorService.setHasError(true);

                router.navigate([`${client.url}/${ERROR_ROUTE}`]);
            }

            if (error.status === HttpStatusCode.SessionExpired) {
                notification.error(errorMessage);
            }

            if (error.status === HttpStatusCode.Locked) {
                notification.error(translate.instant('ERROR_LOCKED'));
            }

            if (error.status === HttpStatusCode.Unauthorized) {
                notification.error(UNAUTHORIZED_MESSAGE, { id: UNAUTHORIZED_TOAST_ID });
            }

            return throwError(() => error);
        })
    );
};

function getErrorMessage(status: number): string {
    switch (status) {
        case HttpStatusCode.BadRequest:
            return 'Bad Request';
        case HttpStatusCode.Unauthorized:
            return 'Unauthorized';
        case HttpStatusCode.Forbidden:
            return 'Forbidden';
        case HttpStatusCode.NotFound:
            return 'Not found';
        case HttpStatusCode.UnsupportedMediaType:
            return 'Unsupported media type';
        case HttpStatusCode.SessionExpired:
            return 'Session expired';
        case HttpStatusCode.Locked:
            return 'Locked';
        case HttpStatusCode.InternalServerError:
            return 'Internal';
        case HttpStatusCode.ServiceUnavailable:
            return 'Service unavailable';
        default:
            return 'An unknown error occurred';
    }
}

function shouldNavigateToErrorPage(status: number): boolean {
    return status === HttpStatusCode.InternalServerError || status === HttpStatusCode.ServiceUnavailable;
}
